import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getUserPaymentsQuery, UserPaymentsData } from 'api/users';
import styles from './Payments.module.scss';

const Payments = () => {
    const { userId } = useParams();
    const [data, setData] = useState<UserPaymentsData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!userId) return;
        setLoading(true);
        getUserPaymentsQuery(userId)
            .then((result) => {
                setData(result);
                setError(null);
            })
            .catch((err) => {
                setError(err.message || 'Failed to load payment data');
            })
            .finally(() => {
                setLoading(false);
            });
    }, [userId]);

    if (loading) {
        return <div className={styles.loading}>Loading payment data...</div>;
    }

    if (error) {
        return <div className={styles.error}>{error}</div>;
    }

    if (!data) {
        return <div className={styles.loading}>No payment data available</div>;
    }

    const formatAmount = (amount: number, currency: string) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency.toUpperCase(),
        }).format(amount / 100);
    };

    const formatDate = (timestamp: number | string) => {
        const date = typeof timestamp === 'number' ? new Date(timestamp * 1000) : new Date(timestamp);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    const getStatusBadgeClass = (status: string, refunded: boolean) => {
        if (refunded) return `${styles.statusBadge} ${styles.statusRefunded}`;
        switch (status) {
            case 'succeeded':
                return `${styles.statusBadge} ${styles.statusSucceeded}`;
            case 'failed':
                return `${styles.statusBadge} ${styles.statusFailed}`;
            default:
                return `${styles.statusBadge} ${styles.statusPending}`;
        }
    };

    const sub = data.currentSubscription;
    const stripeCustomerUrl = `https://dashboard.stripe.com/customers/${data.stripeCustomerId}`;

    return (
        <div className={styles.container}>
            <div className={styles.subscriptionCard}>
                <div className={styles.cardHeader}>
                    <h3>Current Subscription</h3>
                    <a
                        href={stripeCustomerUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.stripeLink}
                    >
                        View in Stripe
                    </a>
                </div>
                {sub ? (
                    <div className={styles.subscriptionDetails}>
                        <div className={styles.detailItem}>
                            <div className={styles.detailLabel}>Plan</div>
                            <div className={styles.detailValue}>{sub.planName}</div>
                        </div>
                        <div className={styles.detailItem}>
                            <div className={styles.detailLabel}>Status</div>
                            <div className={styles.detailValue}>
                                {sub.isTrial ? (
                                    <span className={`${styles.badge} ${styles.badgeTrial}`}>Trial</span>
                                ) : sub.isActive ? (
                                    <span className={`${styles.badge} ${styles.badgeActive}`}>Active</span>
                                ) : (
                                    <span className={`${styles.badge} ${styles.badgeInactive}`}>Inactive</span>
                                )}
                            </div>
                        </div>
                        <div className={styles.detailItem}>
                            <div className={styles.detailLabel}>Start Date</div>
                            <div className={styles.detailValue}>{formatDate(sub.startDate)}</div>
                        </div>
                        <div className={styles.detailItem}>
                            <div className={styles.detailLabel}>Renewal Date</div>
                            <div className={styles.detailValue}>{formatDate(sub.endDate)}</div>
                        </div>
                        {sub.isTrial && sub.trialEndDate && (
                            <div className={styles.detailItem}>
                                <div className={styles.detailLabel}>Trial Ends</div>
                                <div className={styles.detailValue}>{formatDate(sub.trialEndDate)}</div>
                            </div>
                        )}
                    </div>
                ) : (
                    <p className={styles.noSubscription}>No active subscription</p>
                )}
            </div>

            <div className={styles.chargesSection}>
                <h3>Charges</h3>
                {data.charges.length > 0 ? (
                    <table className={styles.chargesTable}>
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Amount</th>
                                <th>Status</th>
                                <th>Description</th>
                                <th>Payment Method</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.charges.map((charge) => (
                                <tr key={charge.id}>
                                    <td>{formatDate(charge.created)}</td>
                                    <td>
                                        {formatAmount(charge.amount, charge.currency)}
                                        {charge.refunded && charge.amountRefunded > 0 && (
                                            <span style={{ color: '#B42318', fontSize: 12, marginLeft: 4 }}>
                                                (-{formatAmount(charge.amountRefunded, charge.currency)})
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        <span className={getStatusBadgeClass(charge.status, charge.refunded)}>
                                            {charge.refunded ? 'Refunded' : charge.status}
                                        </span>
                                    </td>
                                    <td>{charge.description || '-'}</td>
                                    <td>{charge.paymentMethod || '-'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <p className={styles.noCharges}>No charges found for this customer</p>
                )}
            </div>
        </div>
    );
};

export default Payments;
